import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import moment from "moment";

import { getVolumes } from "../../services/notificationService";
import { getRouteUpdatesForClient } from "../../services/deliveryConcernService";
import {
  getClientDefaultLandingPage,
  updateClientDefaultLandingPage,
} from "../../services/accountService";
import { getDateRange, getStartDate } from "../../helpers/date.util";

const QUERY_KEYS = {
  orderVolumeNotification: ["orderVolumeNotification"],
  routeAlerts: ["routeAlerts"],
  defaultLandingPage: ["default-landing-page"],
};

/* Volume Line Chart */

export const useGetOrderVolumeNotification = () =>
  useQuery({
    queryKey: QUERY_KEYS.orderVolumeNotification,
    queryFn: getVolumes,
  });

/**
 * @typedef {Object} VolumeData
 * @property {Date} creationDate
 * @property {number} totalVolume
 * @property {number} ordersCount
 * @property {number} piece
 * @property {number} pallet
 * @property {number} engine
 * @property {number} transmission
 * @property {number} oversize
 * @property {number} parcel
 *
 * @typedef {Object} VolumeTimePeriodOption
 * @property {string} label
 * @property {string} value
 * @property {string} momentUnit
 * @property {number} momentUnitCount
 */

export const volumeTimePeriodOptions = [
  {
    label: "1 Week",
    value: "1W",
    momentUnit: "weeks",
    momentUnitCount: 1,
  },
  {
    label: "2 Weeks",
    value: "2W",
    momentUnit: "weeks",
    momentUnitCount: 2,
  },
  {
    label: "1 Month",
    value: "1M",
    momentUnit: "months",
    momentUnitCount: 1,
  },
  {
    label: "3 Months",
    value: "3M",
    momentUnit: "months",
    momentUnitCount: 3,
  },

  {
    label: "6 Months",
    value: "6M",
    momentUnit: "months",
    momentUnitCount: 6,
  },
  {
    label: "1 Year",
    value: "1Y",
    momentUnit: "years",
    momentUnitCount: 1,
  },
];

export const volumeShipmentMetricOptions = [
  ...[
    {
      label: "Order Count",
      keyValue: "ordersCount",
      description:
        "Note: Order Count is the amount of shipments placed including returns and buyouts",
    },
    { label: "Pieces", keyValue: "piece" },
    { label: "Pallets", keyValue: "pallet" },
    { label: "Engines", keyValue: "engine" },
    {
      label: "Transmissions",
      keyValue: "transmission",
    },
    { label: "Oversized", keyValue: "oversize" },
    { label: "Parcels", keyValue: "parcel" },
  ].sort((current, next) => current.label.localeCompare(next.label)),
  {
    label: "Total Volume",
    keyValue: "totalVolume",
    description:
      "Note: Total Volume is the sum of all volume types (engines, transmissions, oversized, parcels, pallets, pieces)",
  },
];

/**
 * @param {VolumeData[]} [volumeData]
 * @param {VolumeTimePeriodOption} option
 * @returns {VolumeData[]}
 *
 */
export const formatVolumeData = (
  volumeData,
  option = {
    label: "1 Week",
    value: "1W",
    momentUnit: "weeks",
    momentUnitCount: 1,
  }
) => {
  if (!volumeData || !Array.isArray(volumeData)) return [];

  const dailyDateFormat = "MMM DD, YYYY";
  const montlyDateFormat = "MMM YYYY";

  /**
   * @param {Date} currentDate
   * @param {VolumeTimePeriodOption} dateOption
   */
  const convertedDate = (currentDate, dateOption) => {
    const date = moment(currentDate);

    return dateOption.momentUnit === "years" ||
      (dateOption.momentUnit === "months" && dateOption.momentUnitCount > 1)
      ? date.format(montlyDateFormat)
      : date.format(dailyDateFormat);
  };

  const mergedVolumeData = volumeData.reduce((previousData, currentData) => {
    const dateLabel = convertedDate(currentData.creationDate, option);

    const existingLabel = previousData?.find(
      (volume) => volume.dateLabel === dateLabel
    );

    if (!existingLabel) return [...previousData, { ...currentData, dateLabel }];

    return previousData.map((volume) => {
      if (volume?.dateLabel !== dateLabel) return volume;

      return {
        ...volume,
        totalVolume: volume.totalVolume + currentData.totalVolume,
        ordersCount: volume.ordersCount + currentData.ordersCount,
        piece: volume.piece + currentData.piece,
        pallet: volume.pallet + currentData.pallet,
        engine: volume.engine + currentData.engine,
        transmission: volume.transmission + currentData.transmission,
        oversize: volume.oversize + currentData.oversize,
        parcel: volume.parcel + currentData.parcel,
      };
    });
  }, []);

  const startDate = getStartDate(
    moment().format("YYYY-MM-DD"),
    option.momentUnit,
    option.momentUnitCount
  );

  const endDate = moment().format("YYYY-MM-DD");

  const dateRange = getDateRange(
    startDate,
    endDate,
    option.momentUnit === "years" ||
      (option.momentUnit === "months" && option.momentUnitCount > 1)
      ? "months"
      : "days"
  );

  const formatVolumeDataWithDateRange = dateRange.map((date) => {
    const dateLabel = convertedDate(date, option);

    const existingLabel = mergedVolumeData.find(
      (volume) => volume.dateLabel === dateLabel
    );

    if (!existingLabel)
      return {
        dateLabel,
        totalVolume: 0,
        ordersCount: 0,
        piece: 0,
        pallet: 0,
        engine: 0,
        transmission: 0,
        oversize: 0,
        parcel: 0,
      };

    return existingLabel;
  });

  return formatVolumeDataWithDateRange;
};

/* Route Alert Notification */

/**
 * @typedef {Object} RouteAlert
 * @property {number} [tranId]
 * @property {string} [route]
 * @property {string} [clients]
 * @property {string} [subject]
 * @property {string} [e_Body]
 * @property {string} [e_To]
 * @property {string} [enterBy]
 * @property {Date} [entered]
 */

export const useGetRouteAlerts = () =>
  useQuery({
    queryKey: QUERY_KEYS.routeAlerts,
    queryFn: getRouteUpdatesForClient,
    refetchOnMount: false,
  });

/**
 * @param {RouteAlert[]} routeAlerts
 * @returns {RouteAlert[]}
 */
const sortRouteAlerts = (routeAlerts) =>
  routeAlerts.sort((currentRouteAlert, nextRouteAlert) =>
    moment(nextRouteAlert?.entered).diff(moment(currentRouteAlert?.entered))
  );

/**
 * @param {string} [message]
 * @returns {string}
 */
const removeThanksMessage = (message) =>
  message ? message.split("Thank you for choosing Diligent")[0] : "";

/**
 * @param {RouteAlert[]} [routeAlerts]
 * @returns {RouteAlert[]}
 */
export const formattedRouteAlerts = (routeAlerts) => {
  if (!routeAlerts || !Array.isArray(routeAlerts) || !routeAlerts.length)
    return [];

  return sortRouteAlerts(routeAlerts).map((routeAlert) => ({
    ...routeAlert,
    entered: routeAlert?.entered
      ? moment(routeAlert?.entered).format("MM/DD/YYYY")
      : "",
    e_Body: removeThanksMessage(routeAlert.e_Body),
  }));
};

/* Default Landing Page */

export const useGetDefaultLandingPage = () =>
  useQuery({
    queryKey: QUERY_KEYS.defaultLandingPage,
    queryFn: getClientDefaultLandingPage,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

export const useUpdateDefaultLandingPage = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateClientDefaultLandingPage,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.defaultLandingPage,
      });
      toast.success("Default landing page updated successfully");
    },
    onError: () => {
      toast.error("Error updating default landing page");
    },
  });

  return mutation;
};
